// AdminBridge.tsx - Bridge components for Django Admin integration
import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from "@mui/material";
import {
  AdminPanelSettings,
  Launch,
  Description,
  Security,
  Group,
  AccountTree,
  Storage,
  Build,
  Assessment,
} from "@mui/icons-material";
import { useAppContext } from "../../../context/AppContext";

export const AdminBridge: React.FC = () => {
  const { user } = useAppContext();

  if (!user?.isSuperuser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Superuser privileges required.
        </Alert>
      </Box>
    );
  }

  const adminLinks = [
    {
      title: "Django Admin Panel",
      description: "Full Django administrative interface",
      url: "/admin/",
      icon: AdminPanelSettings,
      color: "primary" as const,
      sections: [
        "User Management",
        "Group Management",
        "Site Administration",
        "Content Management",
      ],
    },
    {
      title: "Django Admin Documentation",
      description: "Django admin interface documentation and model references",
      url: "/admin/doc/",
      icon: Description,
      color: "info" as const,
      sections: [
        "Model Documentation",
        "View Documentation",
        "Template Documentation",
        "Bookmarklets",
      ],
    },
  ];

  const handleAdminAccess = (url: string) => {
    // Open in new tab to maintain React SPA session
    window.open(url, "_blank");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Django Admin Integration
      </Typography>

      <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
        Access Django's administrative interface for advanced system management.
        These links open in new tabs to maintain your React session.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Authentication Note:</strong> You'll be automatically logged
          into Django admin using your current session. If prompted to log in,
          use the same credentials as this application.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {adminLinks.map((link) => (
          <Grid size={{ xs: 12, md: 6 }} key={link.url}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <link.icon
                    sx={{ mr: 2, color: `${link.color}.main`, fontSize: 32 }}
                  />
                  <Box>
                    <Typography variant="h6">{link.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {link.description}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  Available features:
                </Typography>
                <List dense>
                  {link.sections.map((section) => (
                    <ListItem key={section} sx={{ py: 0, pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <Typography variant="body2" color="text.secondary">
                          •
                        </Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">{section}</Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>

                <Box
                  sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
                >
                  <Button
                    variant="contained"
                    color={link.color}
                    startIcon={<Launch />}
                    onClick={() => handleAdminAccess(link.url)}
                    fullWidth
                  >
                    Open {link.title}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Admin Actions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Common administrative tasks accessible through Django admin:
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<Group />}
              onClick={() => handleAdminAccess("/admin/users/customuser/")}
              fullWidth
              sx={{ py: 1.5 }}
            >
              User Management
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<Security />}
              onClick={() => handleAdminAccess("/admin/auth/group/")}
              fullWidth
              sx={{ py: 1.5 }}
            >
              Group Permissions
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<AccountTree />}
              onClick={() => handleAdminAccess("/admin/sites/site/")}
              fullWidth
              sx={{ py: 1.5 }}
            >
              Site Configuration
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<Storage />}
              onClick={() =>
                handleAdminAccess("/admin/django_celery_beat/periodictask/")
              }
              fullWidth
              sx={{ py: 1.5 }}
            >
              Scheduled Tasks
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<Build />}
              onClick={() => handleAdminAccess("/admin/")}
              fullWidth
              sx={{ py: 1.5 }}
            >
              Django Admin Home
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<Assessment />}
              onClick={() => handleAdminAccess("/admin/doc/models/")}
              fullWidth
              sx={{ py: 1.5 }}
            >
              Model Reference
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Alert severity="warning">
          <Typography variant="body2">
            <strong>Important:</strong> Changes made through Django admin will
            be reflected in this React application after refresh. For real-time
            updates, use the React admin interface when available.
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};
